/*
    What to show a person when something failed (MPN-1661).

    Every one of these alerts used to be `JSON.stringify(e)`. `JSON.stringify` of an Error
    returns `"{}"` -- Error's own properties are not enumerable -- so a failed upload
    rendered an empty toast. That is how an SVG logo upload could be blocked outright at
    the proxy and look, on screen, like nothing happened at all. It took a screen recording
    to find, which is the real cost of a message that cannot say anything.

    `httpRequest` already puts the server's own text on `err.message`, so preferring that
    surfaces the API's explanation rather than a generic line.
*/

/** A fetch that never reached the server rejects with this and no status. */
const NETWORK_FAILURE = "Could not reach the server. Check your connection and try again.";

export function errorMessage(error: unknown, fallback = "Something went wrong."): string {
  if (typeof error === "string" && error.trim()) return error;

  if (error instanceof Error) {
    // A rejected fetch is a TypeError with no status: the request never completed, so
    // there is no server message to show and "Failed to fetch" tells a person nothing.
    const status = (error as Error & { status?: number }).status;
    if (!status && /failed to fetch|networkerror|load failed/i.test(error.message)) {
      return NETWORK_FAILURE;
    }
    if (error.message) return error.message;
  }

  if (error && typeof error === "object") {
    const asRecord = error as Record<string, unknown>;
    for (const key of ["message", "detail", "error"]) {
      const value = asRecord[key];
      if (typeof value === "string" && value.trim()) return value;
    }
    try {
      const json = JSON.stringify(error);
      // "{}" is the empty-toast case this helper exists to stop.
      if (json && json !== "{}") return json;
    } catch {
      // Circular or otherwise unserialisable; fall through to the fallback.
    }
  }

  return fallback;
}
